import React, { useEffect } from 'react';
import Navbar from '../../components/Navbar';

function usePageMeta(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : '';
    let createdMeta = null;

    document.title = title;
    if (meta) {
      meta.setAttribute('content', description);
    } else {
      createdMeta = document.createElement('meta');
      createdMeta.name = 'description';
      createdMeta.content = description;
      document.head.appendChild(createdMeta);
    }

    return () => {
      document.title = prevTitle;
      if (createdMeta) {
        createdMeta.remove();
      } else if (meta) {
        meta.setAttribute('content', prevDesc || '');
      }
    };
  }, [title, description]);
}

export default function ComplianceChecklistUK() {
  usePageMeta(
    'Digitise Inspection Checklists | Compliance Guide',
    'Learn how businesses can digitise inspection checklists to improve compliance, reduce errors, and pass audits with confidence.'
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50 to-gray-200">
      <Navbar />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <header className="text-center mb-12">
          <p className="text-sm font-semibold text-primary uppercase tracking-wide mb-2">Compliance spotlight</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4">How to Digitise Inspection Checklists for Businesses</h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Paper inspection checklists are still widely used across businesses — but they are slow, error-prone, and risky from a compliance perspective. Digitising inspections improves consistency, reduces errors, and creates audit-ready records.
          </p>
        </header>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why paper inspection checklists no longer work</h2>
          <p className="text-gray-700 mb-4">Paper checklists may feel familiar, but they create serious operational issues:</p>
          <ul className="space-y-2 text-gray-800 list-disc list-inside">
            <li>Checks are completed inconsistently</li>
            <li>Data is hard to review or analyse</li>
            <li>Paper records get lost or damaged</li>
            <li>Audits take longer than necessary</li>
            <li>No real-time visibility for managers</li>
          </ul>
          <p className="text-gray-700 mt-4">For regulated industries, these gaps create compliance risks, not just inefficiencies.</p>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What is a digital inspection checklist?</h2>
          <p className="text-gray-700 mb-4">
            A digital inspection checklist replaces paper forms with structured, mobile-friendly checklists that teams complete on phones, tablets, or desktops.
            Instead of ticking boxes on paper, inspectors can:
          </p>
          <ul className="space-y-2 text-gray-800 list-disc list-inside mb-4">
            <li>Complete checks on-site</li>
            <li>Add photos and notes</li>
            <li>Automatically timestamp results</li>
            <li>Store everything securely in one place</li>
          </ul>
          <p className="text-gray-700">This creates a clear audit trail that is easy to access when you need it most.</p>
        </section>

        <section className="grid md:grid-cols-3 gap-6 mb-10">
          <article className="bg-white rounded-2xl shadow-lg p-6 border border-emerald-100">
            <p className="text-sm font-semibold text-emerald-700 uppercase tracking-wide mb-2">1️⃣ Improved compliance</p>
            <p className="text-gray-700">Digital checklists ensure inspections are completed in full, standardised across teams, and stored securely for future audits.</p>
            <p className="text-gray-700 mt-2">Great for health and safety inspections, quality control, and compliance audits.</p>
          </article>
          <article className="bg-white rounded-2xl shadow-lg p-6 border border-emerald-100">
            <p className="text-sm font-semibold text-emerald-700 uppercase tracking-wide mb-2">2️⃣ Faster inspections and reporting</p>
            <p className="text-gray-700">No manual data entry, no scanning paperwork, and instant access to inspection results. Managers can review inspections as soon as they are completed.</p>
          </article>
          <article className="bg-white rounded-2xl shadow-lg p-6 border border-emerald-100">
            <p className="text-sm font-semibold text-emerald-700 uppercase tracking-wide mb-2">3️⃣ Better accountability</p>
            <p className="text-gray-700">Digital systems record who completed the inspection, when it was done, and what issues were identified. Follow-ups are clearer and audits run smoother.</p>
          </article>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to digitise inspection checklists (step by step)</h2>
          <div className="space-y-6 text-gray-800">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Step 1: Identify your inspection types</h3>
              <p className="mt-2">List all inspections your business performs, such as health and safety inspections, quality control checks, compliance audits, and site or equipment inspections.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Step 2: Standardise your checklists</h3>
              <p className="mt-2">Remove duplicate checks, clarify unclear questions, and ensure inspections align with UK regulations. Standardisation makes digitisation smoother.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Step 3: Choose the right inspection software</h3>
              <p className="mt-2">Look for custom checklist creation, mobile inspections, secure audit trails, and UK-friendly compliance support. This is where specialised inspection software helps.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Step 4: Train your team</h3>
              <p className="mt-2">Keep training simple: show how to complete inspections, explain why consistency matters, and ensure inspectors are confident on mobile devices.</p>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why businesses are moving to digital inspection software</h2>
          <p className="text-gray-700 mb-4">
            UK regulators increasingly expect clear documentation, consistent inspection processes, and easily retrievable records. Digital inspection checklists help businesses prepare for audits, reduce compliance risk, and demonstrate due diligence.
          </p>
          <p className="text-gray-700">For growing teams, digital tools also scale far better than paper systems.</p>
        </section>

        <section className="bg-gradient-to-br from-gray-50 via-emerald-50 to-white border border-emerald-100 rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How BuildMyChecks helps</h2>
          <ul className="space-y-2 text-gray-800 list-disc list-inside mb-4">
            <li>Create custom inspection checklists</li>
            <li>Complete inspections on any device</li>
            <li>Maintain clear compliance records</li>
            <li>Prepare for audits without stress</li>
          </ul>
          <p className="text-gray-700 mb-4">Need to standardise site safety too? See our <a className="text-primary font-semibold hover:text-red-700" href="/health-and-safety-inspection-software">health and safety inspection software</a>.</p>
          <div className="flex flex-wrap gap-3 mt-4">
            <a href="/register" className="inline-flex items-center px-4 py-3 bg-primary text-white rounded-lg font-semibold shadow hover:bg-red-700">
              Get audit-ready without paperwork
            </a>
            <a href="/register" className="inline-flex items-center px-4 py-3 bg-white text-primary border border-primary rounded-lg font-semibold shadow hover:bg-gray-50">
              Start your free trial
            </a>
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Conclusion</h2>
          <p className="text-gray-700">
            Digitising inspection checklists is about compliance, consistency, and confidence. By moving away from paper, businesses reduce risk, save time, and pass audits with less stress. The right digital inspection software makes the transition simple and scalable.
          </p>
        </section>
      </main>
    </div>
  );
}
